import React, { useEffect, useState } from "react";
import { Card, Select } from "antd";
import MaterialTable from "material-table";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import LoadingSpinner from "../../../commonFunctions/loaders/Spinner/LoadingSpinner";
import { GetRevenueReport, RevenueChart } from "../../../api";
import Typography from "@mui/material/Typography";

const { Option } = Select;

function RevenueReport() {
  const [chartData, setChartData] = useState(null);
  const [revenueReportData, setRevenueReportData] = useState(null);
  const [revenueDate, setRevenueDate] = useState("");
  const [revenueAmount, setRevenueAmount] = useState(0);
  const [filter, setFilter] = useState("Max");

  useEffect(() => {
    GetRevenueReport(setRevenueReportData, setRevenueDate, setRevenueAmount);
    RevenueChart(setChartData);
  }, []);

  const isDailyView = ["7 Days", "30 Days", "Month"].includes(filter);

  const formatChartData = (rawChartData, currentFilter) => {
    if (!rawChartData) return [];

    const datasets = {
      Aethenos: "aethenosDataSets",
      Coupons: "couponsDataSet",
      ReferralLinks: "referalLinkDataSets",
      Refunds: "refundsDataSets",
    };

    const aggregatedTotals = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0); 

    const checkIsDaily = ["7 Days", "30 Days", "Month"].includes(currentFilter);

    Object.entries(datasets).forEach(([key, datasetKey]) => {
      if (rawChartData[datasetKey] && Array.isArray(rawChartData[datasetKey])) {
        rawChartData[datasetKey].forEach((entry) => {
          const dateObj = new Date(entry.timestamp);
          dateObj.setHours(0, 0, 0, 0);
          const amount = parseFloat(entry.amount) || 0;

          let formatKey;
          if (checkIsDaily) {
            formatKey = `${String(dateObj.getDate()).padStart(2, "0")}-${String(
              dateObj.getMonth() + 1
            ).padStart(2, "0")}-${dateObj.getFullYear()}`;
          } else {
            formatKey = `${dateObj.getFullYear()}-${String(
              dateObj.getMonth() + 1
            ).padStart(2, "0")}`;
          }

          if (!aggregatedTotals[formatKey]) {
            aggregatedTotals[formatKey] = {
              key: formatKey,
              Aethenos: 0,
              Coupons: 0,
              ReferralLinks: 0,
              Refunds: 0,
              TotalRevenue: 0,
              rawDate: new Date(dateObj),
            };
          }

          aggregatedTotals[formatKey][key] += amount;
          aggregatedTotals[formatKey][key] = parseFloat(
            aggregatedTotals[formatKey][key].toFixed(2)
          );
        });
      }
    });

    let sortedData = [];

    if (checkIsDaily) {
      let daysToSubtract =
        currentFilter === "7 Days" ? 6 : currentFilter === "30 Days" ? 29 : new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate() - 1;

      let startCursor = new Date(today);
      startCursor.setDate(today.getDate() - daysToSubtract);
      let endCursor = new Date(today);

      while (startCursor <= endCursor) {
        const formatKey = `${String(startCursor.getDate()).padStart(2, "0")}-${String(
          startCursor.getMonth() + 1
        ).padStart(2, "0")}-${startCursor.getFullYear()}`;

        if (!aggregatedTotals[formatKey]) {
          aggregatedTotals[formatKey] = {
            key: formatKey,
            Aethenos: 0,
            Coupons: 0,
            ReferralLinks: 0,
            Refunds: 0,
            TotalRevenue: 0,
            rawDate: new Date(startCursor),
          };
        }
        startCursor.setDate(startCursor.getDate() + 1);
      }
    } else {
      const earliestDate =
        Object.values(aggregatedTotals).map((item) => item.rawDate).sort((a, b) => a - b)[0] || new Date(today.getFullYear(), today.getMonth(), 1);

      let startCursor = new Date(earliestDate.getFullYear(), earliestDate.getMonth(), 1);
      const endCursor = new Date(today.getFullYear(), today.getMonth(), 1);

      while (startCursor <= endCursor) {
        const formatKey = `${startCursor.getFullYear()}-${String(startCursor.getMonth() + 1).padStart(2, "0")}`;

        if (!aggregatedTotals[formatKey]) {
          aggregatedTotals[formatKey] = {
            key: formatKey,
            Aethenos: 0,
            Coupons: 0,
            ReferralLinks: 0,
            Refunds: 0,
            TotalRevenue: 0,
            rawDate: new Date(startCursor),
          };
        }
        startCursor.setMonth(startCursor.getMonth() + 1);
      }
    }

    sortedData = Object.values(aggregatedTotals)
      .sort((a, b) => a.rawDate - b.rawDate)
      .map(item => {
        item.TotalRevenue = item.Aethenos + item.Coupons + item.ReferralLinks + item.Refunds;
        return item;
      });

    const getDaysOffset = (days) => {
      const d = new Date(today);
      d.setDate(d.getDate() - days);
      return d;
    };
    
    const getMonthOffset = (months) => {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      d.setMonth(d.getMonth() - months + 1);
      return d;
    };

    switch (currentFilter) {
      case "7 Days":
        sortedData = sortedData.filter((item) => item.rawDate >= getDaysOffset(6));
        break;
      case "30 Days":
        sortedData = sortedData.filter((item) => item.rawDate >= getDaysOffset(29));
        break;
      case "Month":
        const startOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        sortedData = sortedData.filter((item) => item.rawDate >= startOfCurrentMonth);
        break;
      case "6 Months":
        sortedData = sortedData.filter((item) => item.rawDate >= getMonthOffset(6));
        break;
      case "1 Year":
        sortedData = sortedData.filter((item) => item.rawDate >= getMonthOffset(12));
        break;
      case "5 Years":
        sortedData = sortedData.filter((item) => item.rawDate >= getMonthOffset(60));
        break;
      case "YTD":
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        sortedData = sortedData.filter((item) => item.rawDate >= startOfYear);
        break;
      case "Max":
      default:
        break;
    }

    return sortedData.map((item) => {
      let name, tooltipDate;
      
      if (checkIsDaily) {
        name = `${String(item.rawDate.getDate()).padStart(2, "0")}-${String(item.rawDate.getMonth() + 1).padStart(2, "0")}-${item.rawDate.getFullYear()}`;
        tooltipDate = item.rawDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      } else {
        const parts = item.rawDate.toLocaleDateString("en-US", { month: "short", year: "2-digit" }).split(" ");
        name = `${parts[0]} '${parts[1]}`;
        tooltipDate = item.rawDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
      }

      return { ...item, name, tooltipDate };
    });
  };

  const formattedChartData = formatChartData(chartData, filter);

 const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: '10px', borderRadius: '4px', boxShadow: '0px 2px 4px rgba(0,0,0,0.1)' }}>
          <p style={{ margin: '0 0 5px 0', color: '#333', fontWeight: 'bold', fontSize: '13px' }}>
            {item.tooltipDate}
          </p>
          <div className="mt-1">
            {payload.map((entry) => (
              <p key={entry.dataKey} style={{ margin: '2px 0', color: '#555', fontSize: '13px' }}>
                <span style={{ color: entry.color, marginRight: '4px' }}>•</span>
                {entry.dataKey}: <strong>£{entry.value.toFixed(2)}</strong>
              </p>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  if (!chartData) return <LoadingSpinner />;

  return (
    <div className="mb-5 all-courses-container">
      <div className="mb-4">
        <Typography variant="h4" gutterBottom>
          <p className="fs-5 font-bold">Revenue Report</p>
        </Typography>
        <h1 className="font-bold">{revenueAmount}</h1>
        <p className="fs-10">Your lifetime earnings as of {revenueDate}</p>
        <Select
          value={filter}
          onChange={setFilter}
          style={{ width: 150, marginBottom: 20 }}
        >
          <Option value="7 Days">Last 7 days</Option>
          <Option value="30 Days">Last 30 days</Option>
          <Option value="Month">This month</Option>
          <Option value="6 Months">Last 6 months</Option>
          <Option value="YTD">Year to date</Option>
          <Option value="1 Year">Last 12 months</Option>
          <Option value="5 Years">Last 5 years</Option>
          <Option value="Max">Lifetime</Option>
        </Select>
      </div>

      <Card className="p-2">
        <Card className="my-3">
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={formattedChartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                interval="preserveStartEnd"
                height={50}
                tick={{ dy: 10, fontSize: 12, fill: '#666' }}
                tickLine={false}
                axisLine={{ stroke: '#ccc' }}
              />
              <YAxis
                tickFormatter={(value) => `£${value.toFixed(2)}`}
                tick={{ fontSize: 12, fill: '#666' }}
                tickLine={false}
                axisLine={false}
                width={80}
              />
              <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
              <Legend />
              
              <Area
                type="monotone"
                dataKey="Aethenos"
                stroke="#4caf50"
                fill="#4caf50"
                fillOpacity={0.3}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Area
                type="monotone"
                dataKey="Coupons"
                stroke="#fbc02d"
                fill="#fbc02d"
                fillOpacity={0.3}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Area
                type="monotone"
                dataKey="ReferralLinks"
                stroke="#2196f3"
                fill="#2196f3"
                fillOpacity={0.3}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Area
                type="monotone"
                dataKey="Refunds"
                stroke="#f44336"
                fill="#f44336"
                fillOpacity={0.3}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {revenueReportData ? (
          <MaterialTable
            title="Revenue Report"
            columns={[
              {
                title: "Month",
                field: "month",
                render: (rowData) => (
                  <a href={`/performance/revenue-report/${rowData.month}`}>
                    {rowData.month}
                  </a>
                ),
              },
              { title: "Your revenue (£)", field: "yourRevenue", align: "right" },
              { title: "Expected Deposit Date", field: "expectedPaymentDate", align: "right" },
              {
                title: "Payment Status",
                field: "paymentStatus",
                align: "center",
                render: (rowData) => {
                  const revenueAmount = parseFloat(String(rowData.yourRevenue || "0").replace(/,/g, ""));
                  if (revenueAmount === 0) {
                    return <span style={{ color: "#9e9e9e", fontWeight: "bold" }}>-</span>;
                  }
                  const isDeposited = rowData.paymentStatus === "Paid";
                  return (
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "12px",
                        backgroundColor: isDeposited ? "#e8f5e9" : "#fff3e0",
                        color: isDeposited ? "#2e7d32" : "#f57c00",
                        fontWeight: "bold",
                        fontSize: "12px",
                      }}
                    >
                      {isDeposited ? "Paid" : "Pending"}
                    </span>
                  );
                },
              },
            ]}
            data={revenueReportData}
            options={{ sorting: true, exportButton: true }}
          />
        ) : (
          <LoadingSpinner />
        )}
      </Card>
    </div>
  );
}

export default RevenueReport;